import { INPUT_TYPE_LABEL, STAGE_LABEL, isInputType, type InputType, type Stage } from '../kit/stage';
import { CASE_STATUS_KEYS, caseStatus, type CaseStatusKey, type StatusableCase } from '../order/caseStatus';
import { parseClientId, type Patient } from './clients';
import { isLate } from '../time/dueDate';

/**
 * The cases of a doctor or of a patient, searched, narrowed, ordered and paged
 * by the server. The request of 6 October 2026 (fifth), section 4.
 *
 * A doctor may have hundreds of cases, so the table on a doctor's page cannot
 * be a page of them filtered in the browser: every question it can be asked
 * travels as a query, and the answer is a page of exactly the cases that
 * match. The words of that query are here, with what each means, so the
 * screen, the HTTP client, the mock server and the export all read one
 * definition. The same query keeps the screen's filters in its address, so a
 * reload or the Back button restores them.
 */

/**
 * What the cases may be narrowed to by status. `active` is every case still
 * being made: in the lab and not finished, which in the app's words is In
 * production, and On hold at a bench. A case in Ready or in Logistics is not
 * active: the lab's request of 17 October 2026, item 3. The eight in the
 * middle are the statuses a case reads as everywhere (`caseStatus`), for a
 * case that is not archived. `archived` is a case taken off the lists of work
 * in hand. In the order a filter offers them.
 */
export const CLIENT_CASE_STATUSES = ['active', ...CASE_STATUS_KEYS, 'archived'] as const;
export type ClientCaseStatus = 'active' | CaseStatusKey | 'archived';

/** The one status a listed case reads as: one of the eight, or archived. */
export type ListedCaseStatus = CaseStatusKey | 'archived';

/** The order the Status sort puts cases in: as a case lives through them, the archived last. */
export const LISTED_CASE_STATUS_ORDER: readonly ListedCaseStatus[] = [...CASE_STATUS_KEYS, 'archived'];

/** What a case's place in those lists is read from. */
export interface ListedCase extends StatusableCase {
  archive: { archived: boolean };
  dispatch: StatusableCase['dispatch'] & { dispatched: boolean };
}

/** The status a case of the list reads as: archived when it is, otherwise what `caseStatus` says. */
export function listedCaseStatus(order: ListedCase): ListedCaseStatus {
  return order.archive.archived ? 'archived' : caseStatus(order);
}

/**
 * Whether a case is in hand: not dispatched and not archived. A deleted case
 * is on no list at all, so nobody asks it. What Due today and Overdue are
 * among.
 */
export function isInHand(order: Pick<ListedCase, 'archive' | 'dispatch'>): boolean {
  return !order.dispatch.dispatched && !order.archive.archived;
}

/**
 * Whether a case is active: in hand and still being made, so neither in Ready
 * nor in Logistics. The lab's request of 17 October 2026, item 3, read from
 * what a listed case carries: a case in production is not finished, one on
 * hold is while its first kit is at a bench, and every other is finished. This
 * is what `activeCaseCount` counts on a doctor and on a patient, and what
 * "With active cases" means; the API counts it from every kit
 * (`isActiveOrder`).
 */
export function isActiveCase(order: Pick<ListedCase, 'archive' | 'dispatch' | 'state' | 'currentStage'>): boolean {
  if (!isInHand(order)) return false;
  if (order.state === 'production') return true;
  return order.state === 'held' && order.currentStage !== null && order.currentStage !== 'ready';
}

/** Whether a case matches a `status` as it was sent. A word that is none of the ten matches nothing. */
export function matchesCaseStatus(order: ListedCase, status: string): boolean {
  if (status === 'active') return isActiveCase(order);
  return listedCaseStatus(order) === status;
}

/** Where a status stands in the Status sort. */
export function listedCaseStatusRank(status: ListedCaseStatus): number {
  return LISTED_CASE_STATUS_ORDER.indexOf(status);
}

/** The two things the cases may be narrowed to by when they are due, both among the active only. */
export const CLIENT_CASE_DUES = ['today', 'overdue'] as const;
export type ClientCaseDue = (typeof CLIENT_CASE_DUES)[number];

/**
 * Whether a case is due as asked: `today` when its due date is the lab's
 * today, `overdue` when it is before it. Only an active case is either: one
 * that has left the lab, or was archived, is late on nobody's bench. And a
 * finished case is never overdue (every tray Ready): the one rule of
 * src/domain/time/dueDate.ts, the request of 7 October 2026, section 6.
 */
export function matchesCaseDue(
  order: Pick<ListedCase, 'archive' | 'dispatch'> & {
    deliveryDate: string;
    currentStage: string | null;
  },
  due: string,
  today: string,
): boolean {
  if (!isInHand(order)) return false;
  if (due === 'today') return order.deliveryDate === today;
  return due === 'overdue' && isLate(order.deliveryDate, today, order.currentStage === 'ready');
}

/**
 * The orders the cases may be read in: newest booked first, which is what the
 * server answers when none is asked for, oldest first, by due date either way,
 * and by status either way. Two cases that are equal read by job order number.
 */
export const CLIENT_CASE_SORTS = [
  '-createdAt',
  'createdAt',
  'deliveryDate',
  '-deliveryDate',
  'status',
  '-status',
] as const;
export type ClientCaseSort = (typeof CLIENT_CASE_SORTS)[number];
export const DEFAULT_CLIENT_CASE_SORT: ClientCaseSort = '-createdAt';

/** The order asked for, or newest first for nothing and for a word that is not one of the six. */
export function clientCaseSortOf(sort: string | null | undefined): ClientCaseSort {
  return CLIENT_CASE_SORTS.find((each) => each === sort) ?? DEFAULT_CLIENT_CASE_SORT;
}

/**
 * What the cases of a doctor or of a patient are asked for. Everything left
 * off is not narrowed by, and the server's default.
 */
export interface ClientCaseFilter {
  /** Inside the job order number, or the patient's name (a doctor's cases) or the doctor's (a patient's). */
  q?: string;
  status?: ClientCaseStatus;
  /** Lab days `YYYY-MM-DD`, on the day the case was booked, both included. */
  from?: string;
  to?: string;
  /** Any tray of the case has this input: what the app calls the case type. */
  caseType?: InputType;
  /** True for the cases with an urgent tray only. */
  urgent?: boolean;
  due?: ClientCaseDue;
  /** One patient's cases only, among a doctor's. */
  patientId?: string;
  /** One doctor's cases only, among a patient's. */
  doctorId?: string;
  sort?: ClientCaseSort;
  page?: number;
  pageSize?: number;
}

/** The keys a case filter travels under, on the wire and in a screen's address. */
const CASE_FILTER_KEYS = [
  'q',
  'status',
  'from',
  'to',
  'caseType',
  'urgent',
  'due',
  'patientId',
  'doctorId',
  'sort',
  'page',
  'pageSize',
] as const;

/** A lab day as it travels, `2026-10-06`, when it is a real day; otherwise undefined. */
function dayOf(text: string | null): string | undefined {
  if (text === null || !/^\d{4}-\d{2}-\d{2}$/.test(text)) return undefined;
  const at = Date.parse(`${text}T00:00:00Z`);
  return !Number.isNaN(at) && new Date(at).toISOString().slice(0, 10) === text ? text : undefined;
}

/** A whole number above zero, or undefined. */
function countOf(text: string | null): number | undefined {
  return text !== null && /^[1-9]\d*$/.test(text) ? Number(text) : undefined;
}

/** A value with nothing in it is a filter that was not set. */
function textOf(text: string | null): string | undefined {
  const trimmed = (text ?? '').trim();
  return trimmed === '' ? undefined : trimmed;
}

/** One of a list of words, or undefined. */
function wordOf<Word extends string>(words: readonly Word[], text: string | null): Word | undefined {
  return words.find((word) => word === text);
}

/** An object without the keys nothing was set under, so a filter that asks for nothing is `{}`. */
function set<Shape extends object>(shape: Shape): Shape {
  return Object.fromEntries(
    Object.entries(shape).filter(([, value]) => value !== undefined),
  ) as Shape;
}

/**
 * A case filter read from a query: a request's, or a screen's own address.
 * What does not read is left out and never refused, as the server leaves it
 * out, so a stale bookmark still lists. `prefix` is put in front of every key,
 * for a screen that keeps two filters in one address.
 */
export function readClientCaseFilter(params: URLSearchParams, prefix = ''): ClientCaseFilter {
  const get = (key: (typeof CASE_FILTER_KEYS)[number]) => params.get(`${prefix}${key}`);
  const caseType = get('caseType');

  return set<ClientCaseFilter>({
    q: textOf(get('q')),
    status: wordOf(CLIENT_CASE_STATUSES, get('status')),
    from: dayOf(get('from')),
    to: dayOf(get('to')),
    caseType: caseType !== null && isInputType(caseType) ? caseType : undefined,
    urgent: get('urgent') === 'true' ? true : undefined,
    due: wordOf(CLIENT_CASE_DUES, get('due')),
    patientId: parseClientId(get('patientId')) ?? undefined,
    doctorId: parseClientId(get('doctorId')) ?? undefined,
    sort: wordOf(CLIENT_CASE_SORTS, get('sort')),
    page: countOf(get('page')),
    pageSize: countOf(get('pageSize')),
  });
}

/** A filter's values as they travel, key by key, the unset ones left out. */
function caseFilterEntries(filter: ClientCaseFilter): [string, string][] {
  const entries: [string, string | undefined][] = [
    ['q', textOf(filter.q ?? null)],
    ['status', filter.status],
    ['from', filter.from],
    ['to', filter.to],
    ['caseType', filter.caseType],
    // Only ever sent as true: leaving it off is every case.
    ['urgent', filter.urgent === true ? 'true' : undefined],
    ['due', filter.due],
    ['patientId', filter.patientId],
    ['doctorId', filter.doctorId],
    ['sort', filter.sort],
    ['page', filter.page === undefined ? undefined : String(filter.page)],
    ['pageSize', filter.pageSize === undefined ? undefined : String(filter.pageSize)],
  ];
  return entries.filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '');
}

/** The query a case filter is sent as: every key that is set, and nothing else. */
export function clientCaseQuery(filter: ClientCaseFilter): URLSearchParams {
  return new URLSearchParams(caseFilterEntries(filter));
}

/**
 * A screen's address with a case filter written into it: a copy of `params`
 * in which the filter's own keys are replaced and every other key (the tab,
 * the case that is open) is left as it was. What says nothing is left out of
 * the address: an unset filter, the first page, and the order the server
 * reads without being asked, so a page nobody has narrowed has a clean
 * address.
 */
export function writeClientCaseFilter(
  params: URLSearchParams,
  filter: ClientCaseFilter,
  prefix = '',
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of CASE_FILTER_KEYS) next.delete(`${prefix}${key}`);

  for (const [key, value] of caseFilterEntries(filter)) {
    if (key === 'page' && value === '1') continue;
    if (key === 'sort' && value === DEFAULT_CLIENT_CASE_SORT) continue;
    next.set(`${prefix}${key}`, value);
  }
  return next;
}

/**
 * Whether a filter narrows the list at all: what decides that "Reset filters"
 * is offered, and that an empty list reads as nothing matching rather than as
 * no cases. The order and the page narrow nothing.
 */
export function clientCaseFilterNarrows(filter: ClientCaseFilter): boolean {
  const narrowing = { ...filter, sort: undefined, page: undefined, pageSize: undefined };
  return caseFilterEntries(narrowing).length > 0;
}

/* ---- a doctor's patients ---- */

/** A patient's cases with ONE doctor: how many, how many of them active, and when the newest was booked. */
export interface WithDoctor {
  caseCount: number;
  activeCaseCount: number;
  lastCaseAt: string | null;
}

/**
 * A patient as a doctor's page lists one: the patient's record, and beside it
 * what the patient's cases with THIS doctor come to. The record's own counts
 * are over every doctor the patient has been to.
 */
export interface DoctorPatient extends Patient {
  withDoctor: WithDoctor;
}

/**
 * What a doctor's patients may be ordered by, as the `sort` of
 * `GET /doctors/{id}/patients`: the name, how many cases the patient has with
 * this doctor, and when the newest of them was booked. Each reads upward as
 * it stands and downward with a leading minus.
 */
export const DOCTOR_PATIENT_SORT_FIELDS = ['name', 'cases', 'lastCase'] as const;
export type DoctorPatientSortField = (typeof DOCTOR_PATIENT_SORT_FIELDS)[number];
export type DoctorPatientSort = DoctorPatientSortField | `-${DoctorPatientSortField}`;

/**
 * The three orders a screen offers: by name, which is what the server answers
 * when none is asked for, the latest case with this doctor first, and the
 * most cases with this doctor first.
 */
export const DOCTOR_PATIENT_SORTS: readonly DoctorPatientSort[] = ['name', '-lastCase', '-cases'];
export const DEFAULT_DOCTOR_PATIENT_SORT: DoctorPatientSort = 'name';

/**
 * The order a doctor's patients are read in, from what was asked: one of the
 * three words with its direction, or by name upward for nothing and for a
 * word that is not one of them.
 */
export function doctorPatientSortOf(sort: string | null | undefined): {
  field: DoctorPatientSortField;
  descending: boolean;
} {
  const asked = (sort ?? '').trim();
  const descending = asked.startsWith('-');
  const word = descending ? asked.slice(1) : asked;
  const field = DOCTOR_PATIENT_SORT_FIELDS.find((each) => each === word);

  return field === undefined ? { field: 'name', descending: false } : { field, descending };
}

/** Every `sort` the wire takes, for reading one out of an address. */
const DOCTOR_PATIENT_SORT_WORDS: readonly DoctorPatientSort[] = DOCTOR_PATIENT_SORT_FIELDS.flatMap(
  (field): DoctorPatientSort[] => [field, `-${field}`],
);

/** What a doctor's patients are asked for. */
export interface DoctorPatientsFilter {
  /** The patient's name, or the patient's ID (`PT-00031`, or the number). */
  q?: string;
  /** True for the patients with an active case WITH THIS DOCTOR only. */
  active?: boolean;
  sort?: DoctorPatientSort;
  /**
   * With neither `page` nor `pageSize` the server answers every match at
   * once, as it did before the list was paged, and calls it page 1 of a page
   * as large as the list. With either one it pages, fifty to a page unless
   * told.
   */
  page?: number;
  pageSize?: number;
}

const PATIENT_FILTER_KEYS = ['q', 'active', 'sort', 'page', 'pageSize'] as const;

/** A doctor's patients filter read from a query, as `readClientCaseFilter` reads a case filter. */
export function readDoctorPatientsFilter(params: URLSearchParams, prefix = ''): DoctorPatientsFilter {
  const get = (key: (typeof PATIENT_FILTER_KEYS)[number]) => params.get(`${prefix}${key}`);

  return set<DoctorPatientsFilter>({
    q: textOf(get('q')),
    active: get('active') === 'true' ? true : undefined,
    sort: wordOf(DOCTOR_PATIENT_SORT_WORDS, get('sort')),
    page: countOf(get('page')),
    pageSize: countOf(get('pageSize')),
  });
}

function patientFilterEntries(filter: DoctorPatientsFilter): [string, string][] {
  const entries: [string, string | undefined][] = [
    ['q', textOf(filter.q ?? null)],
    ['active', filter.active === true ? 'true' : undefined],
    ['sort', filter.sort],
    ['page', filter.page === undefined ? undefined : String(filter.page)],
    ['pageSize', filter.pageSize === undefined ? undefined : String(filter.pageSize)],
  ];
  return entries.filter((entry): entry is [string, string] => entry[1] !== undefined);
}

/** The query a doctor's patients filter is sent as. */
export function doctorPatientsQuery(filter: DoctorPatientsFilter): URLSearchParams {
  return new URLSearchParams(patientFilterEntries(filter));
}

/** A screen's address with a doctor's patients filter written into it, as `writeClientCaseFilter` writes one. */
export function writeDoctorPatientsFilter(
  params: URLSearchParams,
  filter: DoctorPatientsFilter,
  prefix = '',
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of PATIENT_FILTER_KEYS) next.delete(`${prefix}${key}`);

  for (const [key, value] of patientFilterEntries(filter)) {
    if (key === 'page' && value === '1') continue;
    if (key === 'sort' && value === DEFAULT_DOCTOR_PATIENT_SORT) continue;
    next.set(`${prefix}${key}`, value);
  }
  return next;
}

/** Whether a doctor's patients filter narrows the list: a search, or the active only. */
export function doctorPatientsFilterNarrows(filter: DoctorPatientsFilter): boolean {
  return textOf(filter.q ?? null) !== undefined || filter.active === true;
}

/* ---- the two exports ---- */

/**
 * The two files a doctor's page exports, as the server writes them: their
 * names, their columns and the words in their cells. CSV, as the Archive's
 * export is. The server builds both from the same filter and order as the
 * list on screen, without paging.
 */
export const DOCTOR_CASES_EXPORT_COLUMNS = [
  'Job order',
  'Patient',
  'Case type',
  'Item',
  'Status',
  'Department',
  'Technician',
  // The day the case arrived, under the one word for it. It was Created until 11 October 2026.
  'Received',
  'Due',
  'Urgent',
] as const;

export const DOCTOR_PATIENTS_EXPORT_COLUMNS = [
  'ID',
  'Patient',
  'Cases',
  'Active cases',
  'Last case',
] as const;

/** `ceramica-DR-0012-cases.csv`, from the doctor's ID. */
export function doctorCasesExportName(code: string): string {
  return `ceramica-${code}-cases.csv`;
}

/** `ceramica-DR-0012-patients.csv`. */
export function doctorPatientsExportName(code: string): string {
  return `ceramica-${code}-patients.csv`;
}

/** What the Status column of the export reads, for each status a listed case can have. */
export const CASE_EXPORT_STATUS_LABEL: Record<ListedCaseStatus, string> = {
  production: 'In production',
  ready_deliver: 'Ready to Deliver',
  held: 'On hold',
  paid: 'Pending dispatch',
  awaitingWaybill: 'Awaiting waybill',
  shipped: 'Shipped',
  outForDelivery: 'Out for delivery',
  received: 'Received',
  archived: 'Archived',
};

/** What the Case type column reads: `Digital scan` or `Impression`, and nothing for a case with no tray. */
export function caseExportType(input: InputType | null | undefined): string {
  return input == null ? '' : INPUT_TYPE_LABEL[input];
}

/** What the Department column reads: the stage's name, and nothing once the case has left the lab. */
export function caseExportDepartment(stage: Stage | null | undefined): string {
  return stage == null ? '' : STAGE_LABEL[stage];
}
